import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  collection,
  doc,
  onSnapshot,
  serverTimestamp,
  setDoc
} from "firebase/firestore";
import yaml from "js-yaml";
import { v4 as uuidv4 } from "uuid";
import { auth, db } from "../services/firebase";
import { runApiEndpoint, composeApi } from "../services/functionsClient";
import { logUiEvent } from "../services/analytics";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  DescriptionSection,
  EndpointCodeSection,
  QueryParamsSection,
  SandboxSection,
  IntelligenceSection,
  ToolsSection,
  accordionDetailsSx,
  accordionSummarySx,
  accordionSx,
  accordionTitleStyle
} from "../components/EditAgentPage";

const DEFAULT_CODE = `# Define a handler(inputs, context) function.
def handler(inputs, context):
    # inputs is a dict supplied at runtime
    name = inputs.get("name", "world")
    return {
        "message": f"Hello, {name}!",
        "context": context,
    }
`;

function EditAgentPage() {
  const { agentId: agentIdParam } = useParams();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [swaggerUrl, setSwaggerUrl] = useState("");
  const [swaggerSpec, setSwaggerSpec] = useState("");
  const normalizeMcpServers = useCallback((servers) => {
    if (!servers) return [];
    if (typeof servers === "string") {
      return servers
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean)
        .map((url) => ({ url, name: "" }));
    }
    if (!Array.isArray(servers)) return [];
    return servers
      .map((entry) => {
        if (!entry) return null;
        if (typeof entry === "string") return { url: entry, name: "" };
        if (typeof entry === "object") {
          const url = entry.url || entry.remoteUrl || entry.serverUrl || "";
          const name = entry.name || "";
          if (!url) return null;
          return { url, name };
        }
        return null;
      })
      .filter(Boolean);
  }, []);

  const [mcpServers, setMcpServers] = useState([]);
  const normalizedMcpServers = useMemo(
    () => normalizeMcpServers(mcpServers),
    [mcpServers, normalizeMcpServers]
  );
  const [remoteMcpDocs, setRemoteMcpDocs] = useState("");
  const [code, setCode] = useState(DEFAULT_CODE);
  const [inputs, setInputs] = useState('{ "name": "gofannon" }');
  const [queryParams, setQueryParams] = useState("[]");
  const [autoQueryParams, setAutoQueryParams] = useState(true);
  const [intelligenceEnabled, setIntelligenceEnabled] = useState(true);
  const [model, setModel] = useState("gpt-5-mini");
  const [reasoningEffort, setReasoningEffort] = useState("disable");
  const [builtInTools, setBuiltInTools] = useState("none");
  const [selectedId, setSelectedId] = useState(() => agentIdParam || "");
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [aiStatus, setAiStatus] = useState("");
  const [aiError, setAiError] = useState("");
  const [chatId] = useState(() => uuidv4());
  const [agentId, setAgentId] = useState(() => agentIdParam || uuidv4());
  const [liveQueryParams, setLiveQueryParams] = useState([]);
  const userId = auth.currentUser?.uid;
  const pageTitle = selectedId ? "Edit Agent" : "Build Agent (Dev)";
  const pageSubtitle = selectedId
    ? "Update and test your agent in the sandbox."
    : "Prototype agent endpoints with optional MCP servers or Swagger specs.";

  const parsedQueryParamsList = useMemo(() => {
    return Array.isArray(liveQueryParams) ? liveQueryParams : [];
  }, [liveQueryParams]);

  const parsedInputs = useMemo(() => {
    try {
      return JSON.parse(inputs || "{}") || {};
    } catch (err) {
      return null;
    }
  }, [inputs]);

  useEffect(() => {
    if (agentIdParam) {
      setSelectedId(agentIdParam);
      setAgentId(agentIdParam);
    }
  }, [agentIdParam]);

  useEffect(() => {
    setResult(null);
    setError("");
  }, [selectedId]);

  useEffect(() => {
    if (!userId || !selectedId) return undefined;

    const apiRef = doc(db, "users", userId, "apis", selectedId);
    const unsubscribe = onSnapshot(
      apiRef,
      (snapshot) => {
        if (!snapshot.exists()) return;
        const data = snapshot.data();
        if (data.code) setCode(data.code);
        if (Object.prototype.hasOwnProperty.call(data, "queryParams")) {
          try {
            setQueryParams(JSON.stringify(data.queryParams, null, 2));
          } catch (_) {
            setQueryParams(JSON.stringify(data.queryParams));
          }
          setLiveQueryParams(Array.isArray(data.queryParams) ? data.queryParams : []);
        } else {
          setLiveQueryParams([]);
        }
        if (data.swaggerSpec) setSwaggerSpec(data.swaggerSpec);
        if (data.swaggerUrl) setSwaggerUrl(data.swaggerUrl);
        if (data.mcpServers) setMcpServers(normalizeMcpServers(data.mcpServers));
        if (data.remoteMcpDocs) setRemoteMcpDocs(data.remoteMcpDocs);
        if (Object.prototype.hasOwnProperty.call(data, "intelligenceEnabled")) {
          setIntelligenceEnabled(Boolean(data.intelligenceEnabled));
        }
        if (data.model) setModel(data.model);
        if (data.reasoningEffort) setReasoningEffort(data.reasoningEffort);
        if (data.builtInTools) {
          const toolsList = Array.isArray(data.builtInTools) ? data.builtInTools : [];
          setBuiltInTools(toolsList.length ? toolsList[0] : "none");
        }
        if (data.name) setName(data.name);
        if (data.description) setDescription(data.description);
        setAiStatus("Code updated from Firestore.");
      },
      (snapshotError) => setAiError(snapshotError.message)
    );

    return unsubscribe;
  }, [normalizeMcpServers, selectedId, userId]);

  const normalizeJson = (text) => {
    const trimmed = text.trim();
    const fenceMatch = trimmed.match(/```(?:json)?\n([\s\S]*?)```/i);
    return fenceMatch ? fenceMatch[1].trim() : trimmed;
  };

  const handleSwaggerSpecUpload = useCallback(
    (event) => {
      const file = event.target.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result;
        if (typeof text === "string") {
          setSwaggerSpec(text);
        }
      };
      reader.readAsText(file);
    },
    [setSwaggerSpec]
  );

  const handleRemoteMcpDocsUpload = useCallback(
    (event) => {
      const file = event.target.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result;
        if (typeof text === "string") {
          setRemoteMcpDocs(text);
        }
      };
      reader.readAsText(file);
    },
    [setRemoteMcpDocs]
  );

  const formatSwaggerDocs = (specContent) => {
    if (!specContent) return "";
    let spec;
    try {
      spec = yaml.load(specContent);
    } catch (yamlError) {
      try {
        spec = JSON.parse(specContent);
      } catch (jsonError) {
        return "";
      }
    }

    const baseUrl = Array.isArray(spec?.servers) && spec.servers.length ? spec.servers[0].url || "" : "";
    const docsParts = [`## Tools from OpenAPI spec\n\n**Base URL**: \`${baseUrl}\`\n`];
    const paths = spec?.paths || {};

    Object.entries(paths).forEach(([path, pathItem]) => {
      Object.entries(pathItem || {}).forEach(([method, operation]) => {
        if (!operation || !["get", "post", "put", "delete", "patch", "head", "options"].includes(method.toLowerCase())) return;
        const operationId = operation.operationId || `${method.toUpperCase()}_${path.replace(/\//g, "_")}`;
        const summary = operation.summary || "";
        const descriptionText = operation.description || summary;
        let doc = `### Tool: \`${operationId}\`\n`;
        doc += `**Description**: ${descriptionText}\n`;
        doc += `**Endpoint**: \`HTTP ${method.toUpperCase()}\` \`${baseUrl}${path}\`\n\n`;

        const params = operation.parameters || [];
        if (params.length) {
          const paramsMd = params
            .map((param) => {
              const schema = param.schema || {};
              const type = schema.type || "any";
              const reqStr = param.required ? "required" : "optional";
              const location = param.in || "unknown";
              const description = param.description || "";
              return `- \`${param.name}\` (${type}, in ${location}, ${reqStr}): ${description}`;
            })
            .join("\n");
          doc += `**Parameters**:\n${paramsMd}\n\n`;
        }

        const requestBody = operation.requestBody || {};
        const content = requestBody.content || {};
        if (content["application/json"]) {
          doc += "**Request Body** (JSON):\n";
          doc += `\`\`\`json\n${JSON.stringify(content["application/json"].schema || {}, null, 2)}\n\`\`\`\n\n`;
        }

        doc += "**How to call**:\nUse the http_client to make the request.\n";
        docsParts.push(doc);
      });
    });

    return docsParts.join("\n");
  };

  const formatMcpToolDocs = (tools = [], remoteUrl = "") => {
    if (!Array.isArray(tools) || !tools.length) return "";
    return tools
      .map((tool) => {
        const paramsMdList = [];
        const schema = tool.inputSchema?.properties || {};
        const required = tool.inputSchema?.required || [];
        Object.entries(schema).forEach(([paramName, props]) => {
          const typeStr = (props.type || "Any").replace("integer", "int").replace("string", "str");
          const reqStr = required.includes(paramName) ? "required" : "optional";
          const description = props.description || "";
          paramsMdList.push(`- \`${paramName}\` (${typeStr}, ${reqStr}): ${description}`);
        });
        const paramsMd = paramsMdList.join("\n") || "This tool takes no parameters.";
        return `### Tool: \`${tool.name}\`\n**Description**: ${tool.description || ""}\n\n**Parameters**:\n${paramsMd}\n\n**How to call**:\nUse mcpc[\`${remoteUrl}\`] to call \`${tool.name}\`.\n`;
      })
      .join("\n");
  };

  const fetchMcpDocs = async (remoteUrl) => {
    if (!remoteUrl) return "";
    try {
      const resp = await fetch(`${remoteUrl.replace(/\/$/, "")}/tools`);
      if (!resp.ok) return "";
      const data = await resp.json();
      return formatMcpToolDocs(data.tools || [], remoteUrl);
    } catch (err) {
      console.error("Failed to fetch MCP docs", err);
      return "";
    }
  };

  const generateWithAi = async () => {
    if (!userId) {
      setAiError("You must be signed in to generate code with AI.");
      return;
    }
    if (!description.trim()) {
      setAiError("Tell the AI what you want to build first.");
      return;
    }

    setAiError("");
    setAiStatus("Sending prompt to ChatDev...");
    try {
      const mcpDocs = await fetchMcpDocs(normalizedMcpServers[0]?.url);
      setRemoteMcpDocs(mcpDocs);
      const swaggerDocs = formatSwaggerDocs(swaggerSpec);
      let manualQueryParams = null;
      if (!autoQueryParams) {
        try {
          manualQueryParams = JSON.parse(normalizeJson(queryParams || "[]"));
        } catch (parseErr) {
          setAiError("Query parameters must be valid JSON.");
          setAiStatus("");
          return;
        }
      }
      const basePrompt = `You are an agent code generator. Produce JSON with fields: name, description, swaggerUrl (optional), mcpServers (array), queryParams (array of {name,type,required,description}), code. The code must be a Python function handler(inputs, context) suitable for exec. Requirements: ${description.trim()}.${
        swaggerDocs ? `\n\nHere are docs from a Swagger/OpenAPI spec to ground you:\n${swaggerDocs}` : ""
      }${mcpDocs ? `\n\nHere are docs for MCP tools:\n${mcpDocs}` : ""}${
        manualQueryParams ? `\n\nUse this explicit query parameter list: ${JSON.stringify(manualQueryParams)}.` : "\n\nDecide the best query parameters automatically."
      } Exclude any legacy inputText/outputText parameters. Respond with strict JSON.`;
      const newAgentId = selectedId || agentId;
      const modelsToCall =
        intelligenceEnabled && model && model.trim()
          ? [
              {
                name: model.trim(),
                options: {
                  reasoningEffort: reasoningEffort || "disable",
                  builtInTools: builtInTools === "none" ? [] : [builtInTools]
                }
              }
            ]
          : intelligenceEnabled
            ? []
            : undefined;

      const response = await composeApi({
        userId,
        apiId: newAgentId,
        description,
        swaggerSpec,
        swaggerUrl,
        mcpServers: normalizedMcpServers.map((server) => server.url),
        queryParams: manualQueryParams,
        autoQueryParams,
        message: basePrompt,
        model: model || "gpt-5-mini",
        reasoningEffort: reasoningEffort || "disable",
        builtInTools: builtInTools === "none" ? [] : [builtInTools],
        ...(modelsToCall !== undefined ? { modelsToCall } : {})
      });
      const content = response?.message?.content || "";
      const normalized = normalizeJson(content);
      const parsed = JSON.parse(normalized);
      const formattedQueryParams = parsed.queryParams || (autoQueryParams ? parsed.query_parameters : manualQueryParams);
      const parsedMcpServers = Array.isArray(parsed.mcpServers)
        ? normalizeMcpServers(parsed.mcpServers)
        : normalizedMcpServers;

      setName(parsed.name || name || "Untitled Agent");
      setDescription(parsed.description || description);
      setSwaggerUrl(parsed.swaggerUrl || swaggerUrl);
      if (Array.isArray(parsed.mcpServers)) {
        setMcpServers(parsedMcpServers);
      }
      if (formattedQueryParams) {
        setLiveQueryParams(Array.isArray(formattedQueryParams) ? formattedQueryParams : []);
        setQueryParams(JSON.stringify(formattedQueryParams, null, 2));
      }
      if (parsed.code) {
        setCode(parsed.code);
      }

      const apiRef = doc(db, "users", userId, "apis", newAgentId);
      await setDoc(
        apiRef,
        {
          name: parsed.name || name || "Untitled Agent",
          description: parsed.description || description || "",
          swaggerUrl: parsed.swaggerUrl || swaggerUrl || null,
          swaggerSpec: swaggerSpec || null,
          mcpServers: parsedMcpServers,
          remoteMcpDocs: remoteMcpDocs || null,
          queryParams: formattedQueryParams || [],
          code: parsed.code || code || DEFAULT_CODE,
          model: model || "gpt-5-mini",
          reasoningEffort: reasoningEffort || "disable",
          builtInTools: builtInTools === "none" ? [] : [builtInTools],
          intelligenceEnabled,
          updatedAt: serverTimestamp(),
          createdAt: serverTimestamp()
        },
        { merge: true }
      );

      setSelectedId(newAgentId);
      setAgentId(newAgentId);
      setAiStatus("Draft updated from AI and written to Firestore.");
      logUiEvent("agent_dev_ai_generated", { chatId, hasCode: Boolean(parsed.code), agentId: newAgentId });
    } catch (err) {
      console.error(err);
      setAiError(err.message || "Failed to generate with AI");
      setAiStatus("");
    }
  };

  const saveEndpoint = async () => {
    setSaving(true);
    setError("");
    try {
      if (!userId) {
        setError("You must be signed in to save an endpoint.");
        return;
      }
      const mcpList = normalizedMcpServers;
      let parsedQueryParams = [];
      if (queryParams) {
        try {
          parsedQueryParams = JSON.parse(normalizeJson(queryParams));
        } catch (parseErr) {
          setError("Query parameters must be valid JSON before saving.");
          setSaving(false);
          return;
        }
      }

      const docRef = selectedId
        ? doc(db, "users", userId, "apis", selectedId)
        : doc(collection(db, "users", userId, "apis"));

      await setDoc(
        docRef,
        {
          name: name || "Untitled Agent",
          description: description || "",
          swaggerUrl: swaggerUrl || null,
          swaggerSpec: swaggerSpec || null,
          mcpServers: mcpList,
          remoteMcpDocs: remoteMcpDocs || null,
          queryParams: parsedQueryParams,
          code: code || DEFAULT_CODE,
          model: model || "gpt-5-mini",
          reasoningEffort: reasoningEffort || "disable",
          builtInTools: builtInTools === "none" ? [] : [builtInTools],
          intelligenceEnabled,
          createdAt: serverTimestamp()
        },
        { merge: true }
      );

      setLiveQueryParams(parsedQueryParams);
      setSelectedId(docRef.id);
      setAgentId(docRef.id);
      logUiEvent("agent_dev_saved", { id: docRef.id });
    } catch (err) {
      setError(err.message || "Failed to save endpoint");
    } finally {
      setSaving(false);
    }
  };

  const saveToolSources = useCallback(
    async ({ swaggerUrl: nextSwaggerUrl, mcpServers: nextMcpServers, remoteMcpDocs: nextRemoteMcpDocs }) => {
      if (!userId) {
        const errMsg = "You must be signed in to save an endpoint.";
        setError(errMsg);
        throw new Error(errMsg);
      }

      const docRef = selectedId
        ? doc(db, "users", userId, "apis", selectedId)
        : doc(collection(db, "users", userId, "apis"));

      await setDoc(
        docRef,
        {
          swaggerUrl: nextSwaggerUrl || null,
          swaggerSpec: swaggerSpec || null,
          mcpServers: nextMcpServers || [],
          remoteMcpDocs: nextRemoteMcpDocs || null,
          updatedAt: serverTimestamp(),
          createdAt: serverTimestamp()
        },
        { merge: true }
      );

      setSelectedId(docRef.id);
      setAgentId(docRef.id);
    },
    [selectedId, setError, swaggerSpec, userId]
  );

  const executeEndpoint = async () => {
    if (!userId) {
      setError("You must be signed in to run this endpoint");
      return;
    }
    if (!selectedId) {
      setError("Select or save an endpoint first");
      return;
    }
    if (!parsedInputs) {
      setError("Inputs must be valid JSON");
      return;
    }

    setRunning(true);
    setError("");
    setResult(null);
    try {
      const response = await runApiEndpoint({
        id: selectedId,
        userId,
        inputs: parsedInputs,
        context: { env: "dev" }
      });
      setResult(response.result);
      logUiEvent("agent_dev_executed", { id: selectedId });
    } catch (err) {
      setError(err.message || "Failed to run endpoint");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="grid" style={{ gap: "16px" }}>
      <div className="section-header">
        <h2 className="section-header__title">{pageTitle}</h2>
        <p className="muted">{pageSubtitle}</p>
      </div>

      <div className="section-header" style={{ justifyContent: "flex-end", marginTop: "-8px" }}>
        <button className="button" type="button" onClick={generateWithAi}>
          Build
        </button>
      </div>
      {aiStatus && <p className="muted">{aiStatus}</p>}
      {aiError && <p className="muted" style={{ color: "#b00020" }}>{aiError}</p>}

      <div className="panel">
        <Accordion defaultExpanded disableGutters sx={accordionSx}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="description-content"
            id="description-header"
            sx={accordionSummarySx}
          >
            <div style={accordionTitleStyle}>Description</div>
          </AccordionSummary>
          <AccordionDetails sx={accordionDetailsSx}>
            <DescriptionSection
              description={description}
              setDescription={setDescription}
              name={name}
              setName={setName}
            />
          </AccordionDetails>
        </Accordion>

        <Accordion defaultExpanded disableGutters sx={accordionSx}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="tools-content"
            id="tools-header"
            sx={accordionSummarySx}
          >
            <div style={accordionTitleStyle}>Tools</div>
          </AccordionSummary>
          <AccordionDetails sx={accordionDetailsSx}>
            <ToolsSection
              swaggerUrl={swaggerUrl}
              setSwaggerUrl={setSwaggerUrl}
              swaggerSpec={swaggerSpec}
              setSwaggerSpec={setSwaggerSpec}
              mcpServers={mcpServers}
              setMcpServers={setMcpServers}
              remoteMcpDocs={remoteMcpDocs}
              setRemoteMcpDocs={setRemoteMcpDocs}
              handleSwaggerSpecUpload={handleSwaggerSpecUpload}
              handleRemoteMcpDocsUpload={handleRemoteMcpDocsUpload}
              onSaveToolSources={saveToolSources}
            />
          </AccordionDetails>
        </Accordion>

        <Accordion defaultExpanded disableGutters sx={accordionSx}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="intelligence-content"
            id="intelligence-header"
            sx={accordionSummarySx}
          >
            <div style={accordionTitleStyle}>Intelligence</div>
          </AccordionSummary>
          <AccordionDetails sx={accordionDetailsSx}>
            <IntelligenceSection
              intelligenceEnabled={intelligenceEnabled}
              setIntelligenceEnabled={setIntelligenceEnabled}
              model={model}
              setModel={setModel}
              reasoningEffort={reasoningEffort}
              setReasoningEffort={setReasoningEffort}
              builtInTools={builtInTools}
              setBuiltInTools={setBuiltInTools}
            />
          </AccordionDetails>
        </Accordion>

        <Accordion defaultExpanded disableGutters sx={accordionSx}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="query-params-content"
            id="query-params-header"
            sx={accordionSummarySx}
          >
            <div style={accordionTitleStyle}>Query Parameters</div>
          </AccordionSummary>
          <AccordionDetails sx={accordionDetailsSx}>
            <QueryParamsSection
              autoQueryParams={autoQueryParams}
              setAutoQueryParams={setAutoQueryParams}
              parsedQueryParamsList={parsedQueryParamsList}
              queryParams={queryParams}
              setQueryParams={setQueryParams}
            />
          </AccordionDetails>
        </Accordion>

        <Accordion defaultExpanded disableGutters sx={accordionSx}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="code-content"
            id="code-header"
            sx={accordionSummarySx}
          >
            <div style={accordionTitleStyle}>Endpoint Code</div>
          </AccordionSummary>
          <AccordionDetails sx={accordionDetailsSx}>
            <EndpointCodeSection
              code={code}
              setCode={setCode}
              onResetTemplate={() => setCode(DEFAULT_CODE)}
              onSave={saveEndpoint}
              saving={saving}
            />
          </AccordionDetails>
        </Accordion>

        <Accordion defaultExpanded disableGutters sx={accordionSx}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="sandbox-content"
            id="sandbox-header"
            sx={accordionSummarySx}
          >
            <div style={accordionTitleStyle}>Sandbox</div>
          </AccordionSummary>
          <AccordionDetails sx={accordionDetailsSx}>
            <SandboxSection
              inputs={inputs}
              setInputs={setInputs}
              parsedInputs={parsedInputs}
              executeEndpoint={executeEndpoint}
              running={running}
              error={error}
              result={result}
            />
          </AccordionDetails>
        </Accordion>
      </div>
    </div>
  );
}

export default EditAgentPage;
